import { useAtomValue, useSetAtom } from "jotai";
import { ChevronDown, Clock, CloudRain, PauseCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { isStatusPacketPayload } from "@/api/model/current-lic-packet";
import { RainfallCard } from "@/components";
import ProjectStateCard from "@/components/ProjectStateCard";
import { PropertyOverview } from "@/components/PropertyOverview";
import ReservoirStateCard from "@/components/ReservoirStateCard";
import { appShell, propertiesAtom, rainfallDataByPropertyAtom, selectedPropertyAtom } from "@/store";
import { type LICConsolidateData, licConsolidatedDataAtom, projectsAtom, reservoirsAtom } from "@/store/data";
import { dayjs } from "@/utils/date";
import { getDashboardVirtualDeviceLabel } from "./dashboard-virtual-device";

function DashboardPage() {
  const setBackButton = useSetAtom(appShell.backButtonAtom);
  useEffect(() => {
    setBackButton(false);
  }, [setBackButton]);
  const selectedProperty = useAtomValue(selectedPropertyAtom);
  const properties = useAtomValue(propertiesAtom);
  const hasManyProperties = useMemo(() => properties.length > 1, [properties]);

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Propriedade não encontrada</h2>
          <p className="text-gray-600">A propriedade selecionada não foi encontrada.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {hasManyProperties && <h1 className="text-2xl font-bold text-gray-900 mb-2 ">{selectedProperty.name}</h1>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
        <PropertyOverview showTitle={false} showAddress={false} propertyId={selectedProperty.id} />
      </div>
      {/* Rainfall */}
      <RainfallSection />
      {/* Current Projects State */}
      <ProjectsCurrentStateSection />

      {/* Current Reservoirs State */}
      <ReservoirsCurrentStateSection />

      <LICStateSection />
    </div>
  );
}

export default DashboardPage;

function ProjectsCurrentStateSection() {
  const projects = useAtomValue(projectsAtom);
  const licConsolidatedData = useAtomValue(licConsolidatedDataAtom);

  if (!projects || projects.length === 0) return null;

  if (Object.keys(licConsolidatedData).length === 0) return null;
  return (
    <div className="mt-4 space-y-3">
      <h2 className="text-lg font-semibold text-gray-900">Estado dos Projetos</h2>
      {Object.values(licConsolidatedData).map((data) => (
        <CodecStatusCard key={data.lic.id} data={data} />
      ))}
      <div className="grid grid-cols-1 gap-3">
        {projects.map((project) => (
          <ProjectStateCard key={project.id} project={project} />
        ))}
      </div>
    </div>
  );
}

/**
 * Formats a duration in seconds for display (e.g. "< 1min", "12min", "1h 5min").
 */
const formatTime = (seconds: number): string => {
  if (seconds < 60) return "< 1min";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return `< 1min`;
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
};

function CodecStatusCard({ data }: { data: LICConsolidateData }) {
  const { isPaused, isRaining, pausedElapsedSeconds } = useMemo(() => {
    const statusPacket = data.currentPackets.status;
    if (!statusPacket?.payload_data || !isStatusPacketPayload(statusPacket.payload_data)) {
      return { isPaused: false, isRaining: false, pausedElapsedSeconds: 0 };
    }

    const status = statusPacket.payload_data.status;
    // The device reports the pause state in `scheduling_paused`. `raining` is an
    // independent flag: on the CODEC, rain alone does not pause scheduling (only
    // reaching the 24h rainfall limit or a manual pause command does), so it must
    // not force the "Pausado" state. `paused_time` is the number of minutes
    // elapsed since the pause started (see
    // engineering/codec_wifi/components/proto/status.proto).
    return {
      isPaused: !!status.scheduling_paused,
      isRaining: !!status.raining,
      pausedElapsedSeconds: (status.paused_time ?? 0) * 60,
    };
  }, [data.currentPackets.status]);

  if (!isPaused) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow">
      <div className="w-full p-5 flex flex-row justify-between items-center gap-4">
        <div className="flex-1 text-left">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {data.lic.metadata?.label
              ? `${data.lic.metadata.label} (${data.lic.device.identifier})`
              : data.lic.device.identifier}
          </h3>
        </div>

        <div className="flex-shrink-0 flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 text-sm font-medium text-red-800 bg-red-100 px-3 py-1 rounded-full"
            aria-label="Status: Pausado"
          >
            <PauseCircle size={16} aria-hidden="true" />
            Pausado
          </span>
        </div>
      </div>

      <div className="px-4 py-2 text-gray-600 border-t border-gray-200 bg-gray-50 text-sm space-y-2">
        <div className="flex items-center gap-2">
          <Clock size={16} />
          <span className="font-medium">Pausado há {formatTime(pausedElapsedSeconds)}</span>
        </div>
        {isRaining && (
          <div className="flex items-center gap-2">
            <CloudRain size={16} />
            <span className="font-medium">Chuva Detectada</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ReservoirsCurrentStateSection() {
  const reservoirs = useAtomValue(reservoirsAtom);
  if (!reservoirs || reservoirs.length === 0) return null;
  return (
    <div className="mt-4 space-y-3">
      <h2 className="text-lg font-semibold text-gray-900">Estado dos Reservatórios</h2>
      <div className="grid grid-cols-1 gap-3">
        {reservoirs.map((r) => (
          <ReservoirStateCard key={r.id} reservoirId={r.id} />
        ))}
      </div>
    </div>
  );
}

function RainfallSection() {
  const selectedProperty = useAtomValue(selectedPropertyAtom);
  const rainfall = useAtomValue(rainfallDataByPropertyAtom);
  if (!selectedProperty) return null;
  // If disabled or no LIC provided rain data, hide the section
  if (!selectedProperty.rain_gauge_enabled || rainfall.gaugeCount === 0) {
    return null;
  }
  return (
    <div className="mt-4 space-y-3">
      <h2 className="text-lg font-semibold text-gray-900">Chuva</h2>
      <div className="grid grid-cols-1 gap-3">
        <RainfallCard />
      </div>
    </div>
  );
}

function LICStateSection() {
  const licConsolidatedData = useAtomValue(licConsolidatedDataAtom);
  if (Object.keys(licConsolidatedData).length === 0) return null;
  return (
    <div className="mt-4 space-y-3">
      <h2 className="text-lg font-semibold text-gray-900">Dispositivos</h2>
      <div className="grid grid-cols-1 gap-3">
        {Object.values(licConsolidatedData).map((data) => (
          <LICStateCard key={data.lic.id} data={data} />
        ))}
      </div>
    </div>
  );
}
function LICStateCard({ data }: { data: LICConsolidateData }) {
  const [expanded, setExpanded] = useState(false);

  const getStateBadge = (vd: LICConsolidateData["virtualDevices"][number]) => {
    const usesInput = vd.virtualDevice?.elementType === "reservoir";
    const turnedOn = usesInput ? vd.state?.inputOn : vd.state?.outputOn;
    if (!vd.state) {
      return <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md">Sem estado</span>;
    }
    const badges = [];
    if (vd.state.failed) {
      badges.push(
        <span key="failed" className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-md">
          Falha
        </span>
      );
    }
    badges.push(
      turnedOn ? (
        <span key="on" className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-md">
          Ligado
        </span>
      ) : (
        <span key="off" className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md">
          Desligado
        </span>
      )
    );

    badges.push(
      vd.state.synced ? (
        <span key="synced" className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-md">
          Sincronizado
        </span>
      ) : (
        <span key="unsynced" className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-md">
          Não Sincronizado
        </span>
      )
    );

    return <div className="flex flex-wrap gap-1">{badges}</div>;
  };

  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      role="region"
      aria-label={`Estado do LIC ${data.lic.metadata?.label || data.lic.device.identifier}`}
    >
      {/* Header / Summary */}
      <button
        type="button"
        className="w-full p-4 text-left transition-colors flex items-start justify-between gap-4 bg-white"
        onClick={() => setExpanded((e: boolean) => !e)}
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {data.lic.metadata?.label
                ? `${data.lic.metadata.label} (${data.lic.device.identifier})`
                : data.lic.device.identifier}
            </h3>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? "rotate-180" : "rotate-0"}`}
            />
          </div>

          <div className="mt-2 space-y-2">
            <div className="text-sm text-gray-600">
              <span className="block text-xs text-gray-500">Último pacote</span>
              <span className="text-gray-800">
                {data.latestPacket ? dayjs(data.latestPacket.packet_date).calendar() : "Nenhum pacote recebido"}
              </span>
            </div>

            <div className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">Dispositivos:</span>{" "}
              {`${data.mappedDevices.length} mesh, ${
                data.virtualDevices.length
              } ${data.virtualDevices.length === 1 ? "virtual" : "virtuais"}, ${
                data.virtualDeviceStateCount.synced
              } sincronizado(s), ${data.virtualDeviceStateCount.outputOn} saída(s) ativa(s), ${
                data.virtualDeviceStateCount.inputOn
              } entrada(s) ativa(s), ${data.virtualDeviceStateCount.failed} falha(s)`}
            </div>
          </div>
        </div>
      </button>

      {/* Details: virtual devices list */}
      {expanded && (
        <div className="border-t border-gray-100">
          {data.virtualDevices.length === 0 ? (
            <div className="p-4 text-sm text-gray-600">Nenhum dispositivo virtual cadastrado.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.virtualDevices.map((vd) => (
                <li key={vd.virtualDevice.idx} className="flex items-center justify-between p-3">
                  <span className="text-sm text-gray-800 truncate">{getDashboardVirtualDeviceLabel(vd)}</span>
                  {getStateBadge(vd)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
